"use client";

import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { authClient } from "~/lib/auth/client";
import { api } from "~/trpc/react";

export function PostHogIdentity() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    return authClient.onAuthChange(setIsAuthenticated);
  }, []);

  const { data: user } = api.user.getUser.useQuery(undefined, {
    enabled: isAuthenticated === true,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    try {
      if (user) {
        posthog.identify(user.id, {
          name: `${user.first_name} ${user.last_name}`,
          is_host: user.is_host,
          ...(user.email ? { email: user.email } : {}),
          ...(user.phone ? { phone: user.phone } : {}),
        });
        // `$set` merges, so omitting a key leaves the previous value on the
        // person. The API nulls either contact when one is reassigned to
        // another account (releaseReassignedContacts), which is exactly when a
        // stale value is worst: searching PostHog for a number would return
        // both the person who used to own it and the person who owns it now.
        //
        // Both contacts go through `$unset` rather than a null value, matching
        // AnalyticsProvider on mobile. Either representation reads the same
        // through PostHog's presence operators ("is not set" matches a property
        // that "does not exist or has no value"), but the two clients writing
        // the same person must agree, or a cohort answers differently depending
        // on which one the person last used. `$unset` is the documented removal
        // mechanism, so it wins the tie.
        //
        // One combined call, not two: unsetPersonProperties forces a feature
        // flag reload and posthog-js exposes no way to opt out, so a second
        // call would buy a second /flags round trip for nothing.
        const unset: string[] = [];
        if (!user.email) unset.push("email");
        if (!user.phone) unset.push("phone");
        if (unset.length > 0) {
          posthog.unsetPersonProperties(unset);
        }
      }
    } catch {
      // PostHog identity failed — app continues normally
    }
  }, [user]);

  return null;
}
